import { useNotifications } from '@shared/hooks/useNotifications';
import { useProfile } from '@shared/hooks/useProfile';
import { useProfanityStore } from '@shared/services/profanity.service';
import { NotificationType } from '@shared/types/notification.types';
import { User } from '@shared/types/user';
import { containsProfanity } from '@shared/utils/profanityFilter';
import {
	addDoc,
	collection,
	doc,
	getDoc,
	getDocs,
	limit,
	onSnapshot,
	orderBy,
	query,
	QueryDocumentSnapshot,
	startAfter,
	Timestamp,
	updateDoc,
	where,
} from 'firebase/firestore';
import { useCallback, useEffect, useRef, useState } from 'react';
import { db } from '../../../config/firebase';
import { MessageNotificationsService } from '../services/message-notifications.service';
import { Message, MessageType } from '../types';
import { useMessageUpdates } from './useMessageUpdates';

/**
 * Hook to manage messages in a conversation
 * Handles real-time updates, pagination, and message operations
 */
export const useMessages = (
	conversationId: string,
	selectedUser: User | null,
	userMap?: Record<string, User> // New parameter for group notifications
) => {
	const { user } = useProfile();
	const [messages, setMessages] = useState<Message[]>([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [hasMore, setHasMore] = useState(true);
	const lastMessageRef = useRef<QueryDocumentSnapshot | null>(null);
	const isLoadingMoreRef = useRef(false);
	const messagesPerPage = 20;

	// Track unsent messages for email notification
	const unsentMessagesRef = useRef<Message[]>([]);
	const notificationTimerRef = useRef<NodeJS.Timeout | null>(null);

	const { updateTypingStatus, addMessageUpdate, isTyping } = useMessageUpdates(
		conversationId,
		messages,
		setMessages
	);

	const { createNotification } = useNotifications();

	// Initial messages load and real-time updates
	useEffect(() => {
		// Reset state when conversation changes
		setMessages([]);
		setLoading(true);
		setError(null);
		setHasMore(true);
		lastMessageRef.current = null;
		isLoadingMoreRef.current = false;

		// Clear unsent messages and notification timer when conversation changes
		unsentMessagesRef.current = [];
		if (notificationTimerRef.current) {
			clearTimeout(notificationTimerRef.current);
			notificationTimerRef.current = null;
		}

		if (!conversationId) {
			setLoading(false);
			return;
		}

		setLoading(true);
		setError(null);

		// Query for most recent messages
		const q = query(
			collection(db, 'conversations', conversationId, 'messages'),
			where('deleted', '!=', true),
			orderBy('timestamp', 'desc'),
			limit(messagesPerPage)
		);

		// Subscribe to real-time updates
		const unsubscribe = onSnapshot(
			q,
			(snapshot) => {
				const changes = snapshot.docChanges();

				// Process only added or modified messages
				changes.forEach((change) => {
					if (change.type === 'added' || change.type === 'modified') {
						const newMessage = {
							id: change.doc.id,
							...change.doc.data(),
							timestamp: change.doc.data().timestamp?.toDate(),
						} as Message;

						setMessages((prev) => {
							// Check if message already exists
							const messageIndex = prev.findIndex(
								(msg) => msg.id === newMessage.id
							);

							if (messageIndex !== -1) {
								// Update existing message
								const updatedMessages = [...prev];
								updatedMessages[messageIndex] = newMessage;
								return updatedMessages;
							} else {
								// Add new message
								return [...prev, newMessage].sort(
									(a, b) => a.timestamp.getTime() - b.timestamp.getTime()
								);
							}
						});

						// Mark message as read if it's not from current user
						if (!newMessage.read && newMessage.senderId !== user?.id) {
							updateDoc(
								doc(
									db,
									'conversations',
									conversationId,
									'messages',
									newMessage.id,
								),
								{ read: true }
							);

							// Track received message for consolidated email notification
							trackMessageForNotification(newMessage);
						}
					}
				});

				// Update pagination state
				if (snapshot.docs.length > 0) {
					lastMessageRef.current = snapshot.docs[snapshot.docs.length - 1];
					setHasMore(snapshot.docs.length === messagesPerPage);
				}

				setLoading(false);
			},
			(err) => {
				console.error('Error loading messages:', err);
				setError(err.message);
				setLoading(false);
			}
		);

		return () => {
			unsubscribe();

			// Send any pending messages when component unmounts
			/* if (unsentMessagesRef.current.length > 0 && selectedUser?.id) {
			  MessageNotificationsService.sendConsolidatedNotifications(
			  conversationId,
			  selectedUser.id,
			  unsentMessagesRef.current
			  ).catch((error) => {
			  console.error(
				'Failed to send consolidated message notification on unmount:',
				error
			  );
			  });
			} */

			// Clear timer
			if (notificationTimerRef.current) {
				clearTimeout(notificationTimerRef.current);
				notificationTimerRef.current = null;
			}
		};
	}, [conversationId, user?.id]);

	/**
	 * Load older messages for pagination
	 * Returns true if more messages were loaded
	 */
	const loadMoreMessages = async () => {
		if (
			!hasMore ||
			!lastMessageRef.current ||
			loading ||
			isLoadingMoreRef.current
		) {
			return false;
		}

		try {
			isLoadingMoreRef.current = true;

			const q = query(
				collection(db, 'conversations', conversationId, 'messages'),
				where('deleted', '!=', true),
				orderBy('timestamp', 'desc'),
				startAfter(lastMessageRef.current),
				limit(messagesPerPage)
			);

			const snapshot = await getDocs(q);

			if (snapshot.empty) {
				setHasMore(false);
				return false;
			}

			const olderMessages = snapshot.docs.map((doc) => ({
				id: doc.id,
				...doc.data(),
				timestamp: doc.data().timestamp?.toDate(),
			})) as Message[];

			// Update last document reference
			lastMessageRef.current = snapshot.docs[snapshot.docs.length - 1];

			// Sort chronologically and merge with existing messages
			const sortedOlderMessages = olderMessages.sort(
				(a, b) => a.timestamp.getTime() - b.timestamp.getTime()
			);

			setMessages((prev) => {
				// Filter out any potential duplicates
				const existingIds = new Set(prev.map((msg) => msg.id));
				const uniqueNewMessages = sortedOlderMessages.filter(
					(msg) => !existingIds.has(msg.id)
				);

				if (uniqueNewMessages.length === 0) {
					setHasMore(false);
					return prev;
				}

				return [...uniqueNewMessages, ...prev];
			});

			setHasMore(snapshot.docs.length === messagesPerPage);
			return true;
		} catch (err: any) {
			console.error('Error loading more messages:', err);
			setError(err.message);
			return false;
		} finally {
			isLoadingMoreRef.current = false;
		}
	};

	/**
	 * Schedule email notification for consolidated messages
	 * This will send an email after 1 minute of inactivity
	 */
	const scheduleEmailNotification = useCallback(() => {
		// Clear any existing timer
		if (notificationTimerRef.current) {
			console.log('Clearing existing timer');
			clearTimeout(notificationTimerRef.current);
			notificationTimerRef.current = null;
		}

		console.log('Setting new timer');

		// Set new timer for 15 seconds (15000ms)
		notificationTimerRef.current = setTimeout(async () => {
			try {
				console.log('Sending consolidated notification', unsentMessagesRef.current, selectedUser?.id);
				// Only send if we have unsent messages and a valid recipient
				if (unsentMessagesRef.current.length > 0) {
					// Get conversation data to identify the receiver
					const conversationRef = doc(db, 'conversations', conversationId);
					const conversationSnap = await getDoc(conversationRef);
					const conversationData = conversationSnap.data();
					const receiversIds = conversationData.participants.filter(
						(id: string) => id !== user.id
					)?.map((id: string) => id);
					// Send consolidated notification
					await MessageNotificationsService.sendConsolidatedNotifications(
						conversationId,
						receiversIds,
						unsentMessagesRef.current
					);

					// Clear unsent messages after successful notification
					unsentMessagesRef.current = [];
					console.log('Sent consolidated message notification email');
				}
			} catch (error) {
				console.error(
					'Failed to send consolidated message notification:',
					error
				);
			} finally {
				notificationTimerRef.current = null;
			}
		}, 15000); // 15 seconds delay
	}, [conversationId, selectedUser?.id]);

	/**
	 * Track message for consolidated email notification
	 */
	const trackMessageForNotification = useCallback(
		(message: Message) => {
			// Add message to unsent messages
			unsentMessagesRef.current.push(message);

			console.log('Tracking message for notification:', message);

			// Schedule notification
			scheduleEmailNotification();
		},
		[scheduleEmailNotification]
	);

	const { openProfanityModal } = useProfanityStore();

	const sendMessage = async (
		content: string,
		type: MessageType = 'text',
		mediaUrl?: string
	) => {
		try {
			if (!user?.id) return;

			if (type === 'text' && containsProfanity(content)) {
				openProfanityModal();
				return;
			}

			// Get conversation data to identify the receiver
			const conversationRef = doc(db, 'conversations', conversationId);
			const conversationSnap = await getDoc(conversationRef);

			if (!conversationSnap.exists() || conversationSnap.data()?.deleted) return;

			const conversationData = conversationSnap.data();
			const receiverId = conversationData.participants.find(
				(id: string) => id !== user.id
			);

			// Create message object
			const messageData = {
				content,
				senderId: user.id,
				timestamp: Timestamp.now(),
				read: false,
				deleted: false,
				type,
				...(mediaUrl && { mediaUrl }),
			};

			// Add to Firestore
			const docRef = await addDoc(
				collection(db, 'conversations', conversationId, 'messages'),
				messageData
			);

			console.log('Message sent:', messageData);

			// Track message for consolidated email notification
			const message = {
				id: docRef.id,
				...messageData,
				timestamp: messageData.timestamp.toDate(),
				deleted: false,
			} as Message;

			// Notifications: direct vs group
			if (
				Array.isArray(conversationData.participants) &&
				conversationData.participants.length > 2 &&
				userMap
			) {
				console.log(
					'Sending group notification',
					conversationData.participants
				);
				// Group: notify all except sender
				for (const participantId of conversationData.participants) {
					if (participantId === user.id) continue;
					const participantUser = userMap[participantId];
					if (participantUser?.settings?.notificationSettings?.messages) {
						await createNotification({
							receiverId: participantId,
							type: NotificationType.NEW_MESSAGE,
							metadata: {
								actorId: user.id,
								conversationId,
								messageType: type,
								preview:
									type === 'text' ? content.substring(0, 100) : `[${type}]`,
							},
						});
					}
				}
			} else if (
				receiverId &&
				selectedUser?.settings?.notificationSettings?.messages
			) {
				console.log('Sending direct notification');
				// Direct: notify the other user
				await createNotification({
					receiverId,
					type: NotificationType.NEW_MESSAGE,
					metadata: {
						actorId: user.id,
						conversationId,
						messageType: type,
						preview: type === 'text' ? content.substring(0, 100) : `[${type}]`,
					},
				});

			}
			trackMessageForNotification(message);
		} catch (error) {
			console.error('Error sending message:', error);
			throw error;
		}
	};

	const deleteMessage = useCallback(
		async (messageId: string) => {
			if (!conversationId || !user) return;

			try {
				const messageRef = doc(
					db,
					'conversations',
					conversationId,
					'messages',
					messageId
				);
				const messageDoc = await getDoc(messageRef);

				if (!messageDoc.exists()) {
					throw new Error('Message not found');
				}

				const messageData = messageDoc.data();
				if (messageData.senderId !== user.id) {
					throw new Error('You can only delete your own messages');
				}

				// Update message type to deleted
				await updateDoc(messageRef, {
					type: 'deleted',
					content: '',
					mediaUrl: null,
				});

				// Add update notification
				await addMessageUpdate(messageId, 'delete');

				// Update last message if needed
				const lastMessage = messages[messages.length - 1];
				if (lastMessage?.id === messageId) {
					await updateDoc(doc(db, 'conversations', conversationId), {
						lastMessage: {
							...lastMessage,
							type: 'deleted',
							content: '',
							mediaUrl: null,
						},
					});
				}
			} catch (error: any) {
				console.error('Error deleting message:', error);
				throw error;
			}
		},
		[conversationId, user, messages, addMessageUpdate]
	);

	const EDIT_TIME_LIMIT_MS = 5 * 60 * 1000;

	const editMessage = useCallback(
		async (messageId: string, newContent: string) => {
			if (!conversationId || !user) return;

			if (containsProfanity(newContent)) {
				openProfanityModal();
				return;
			}

			try {
				const messageRef = doc(
					db,
					'conversations',
					conversationId,
					'messages',
					messageId
				);
				const messageDoc = await getDoc(messageRef);

				if (!messageDoc.exists()) {
					throw new Error('Message not found');
				}

				const messageData = messageDoc.data();

				if (messageData.senderId !== user.id) {
					throw new Error('You can only edit your own messages');
				}

				const sentAt = messageData.timestamp?.toDate?.() ?? new Date(messageData.timestamp);
				if (Date.now() - sentAt.getTime() > EDIT_TIME_LIMIT_MS) {
					throw new Error('Edit time limit exceeded');
				}

				await updateDoc(messageRef, {
					content: newContent,
					edited: true,
					editedAt: Timestamp.now(),
				});

				await addMessageUpdate(messageId, 'edit');

				const lastMessage = messages[messages.length - 1];
				if (lastMessage?.id === messageId) {
					await updateDoc(doc(db, 'conversations', conversationId), {
						lastMessage: {
							...lastMessage,
							content: newContent,
							edited: true,
						},
					});
				}
			} catch (error: any) {
				console.error('Error editing message:', error);
				throw error;
			}
		},
		[conversationId, user, messages, addMessageUpdate, openProfanityModal]
	);

	return {
		messages,
		loading,
		error,
		sendMessage,
		deleteMessage,
		editMessage,
		loadMoreMessages,
		hasMore,
		updateTypingStatus,
		isTyping,
	};
};
